import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { Card, Skeleton, Typography, Grid } from "@mui/material";
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import axiosInstance from "../AxiosInstance";

interface Forecast {
    forecastId: number;
    name: string;
    startPeriod: number;
    startDate: string;
    forecastPeriods: ForecastPeriod[];
}

interface ForecastPeriod {
    forecastPeriodId: number;
    forecastId: number;
    name: string;
    isLocked: boolean;
    periodIndex: number;
    hasForcastWeekElapsed: boolean;
}

interface ChartSeries {
    label: string;
    data: number[];
}



interface TopCustomerData {
    customerId: number;
    customerName: string;
    value: number;
    units: number;
}

interface TopSkuData {
    productId: number;
    skuDescription: string;
    value: number;
    units: number;
}

interface DashboardChartData {
    startMonth: number;
    mainChart: ChartSeries[];
    topCustomers: TopCustomerData[];
    topSkus: TopSkuData[];
}

interface DatasetItem {
    label: string;
    [key: string]: number | string;
}

const Home: React.FC = () => {
    const [loadingForecasts, setLoadingForecasts] = useState<boolean>(true);
    const [forecasts, setForecasts] = useState<Forecast[]>([]);
    const [selectedForecastId, setSelectedForecastId] = useState<number | null>(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState<number | null>(null);
    const [mainChartData, setMainChartData] = useState<ChartSeries[]>([]);
    const [dataset, setDataset] = useState<DatasetItem[]>([]);
    const [loadingChart, setLoadingChart] = useState<boolean>(false);
    const [topCustomersData, setTopCustomersData] = useState<TopCustomerData[]>([]);
    const [topSkusData, setTopSkusData] = useState<TopSkuData[]>([]);

    const getToken = (): string | null => localStorage.getItem("token");

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem("selectedForecast") || "{}");
        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        } else {
            setSelectedForecastPeriodId(null);
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async (): Promise<void> => {
        try {
            setLoadingForecasts(true);
            const token = getToken();
            const response = await axiosInstance.get<Forecast[]>("/forecasts", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setForecasts(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const handleForecastSelected = (id: number): void => {
        localStorage.setItem("selectedForecast", JSON.stringify({ forecastId: id }));
        setSelectedForecastId(id);
        setSelectedForecastPeriodId(null);
    };

    const handleForecastPeriodSelected = (forecastPeriodId: number | null): void => {
        if (forecastPeriodId) {
            localStorage.setItem("selectedForecastPeriodId", forecastPeriodId.toString());
            setSelectedForecastPeriodId(forecastPeriodId);
        } else {
            localStorage.removeItem("selectedForecastPeriodId");
            setSelectedForecastPeriodId(null);
        }
    };

    const fetchDashboardData = async (): Promise<void> => {
        if (!selectedForecastId || !selectedForecastPeriodId) return;

        try {
            setLoadingChart(true);
            const response = await axiosInstance.get<DashboardChartData>("/dashboard", {
                params: {
                    forecastId: selectedForecastId,
                    forecastPeriodId: selectedForecastPeriodId,
                },
            });

            const data = response.data;
            const startMonth = data.startMonth;

            const months = generateMonthLabels(startMonth);

            const newDataset: DatasetItem[] = months.map((month, index) => ({
                label: month,
                ...data.mainChart?.reduce((acc: Record<string, number>, series: ChartSeries) => {
                    acc[series.label] = series.data[index] || 0;
                    return acc;
                }, {} as Record<string, number>),
            }));

            setDataset(newDataset);
            setMainChartData(data.mainChart || []);
            setTopCustomersData(data.topCustomers || []);
            setTopSkusData(data.topSkus || []);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setMainChartData([]);
            setDataset([]);
            setTopCustomersData([]);
            setTopSkusData([]);
        } finally {
            setLoadingChart(false);
        }
    };

    useEffect(() => {
        if (selectedForecastId && selectedForecastPeriodId) {
            fetchDashboardData();
        }
    }, [selectedForecastId, selectedForecastPeriodId]);

    const selectedForecast = forecasts.find((f) => f.forecastId === selectedForecastId);

    const generateMonthLabels = (startMonth: number): string[] => {
        const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
        return months.slice(startMonth - 1).concat(months.slice(0, startMonth - 1));
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatUnits = (value: number): string => {
        return new Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const pieChartColors = ['#1a237e', '#3f51b5', '#5c6bc0', '#90caf9', '#bbdefb'];

    return (
        <Box className="component-container">
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 2 }}>
                {loadingForecasts ? (
                    <Skeleton variant="rectangular" width={200} height={40} />
                ) : (
                    <ForecastDropdown
                        forecasts={forecasts}
                        selectedForecastId={selectedForecastId}
                        onForecastSelected={handleForecastSelected}
                    />
                )}
                {loadingForecasts ? (
                    <Skeleton variant="rectangular" width={200} height={40} />
                ) : (
                    <ForecastPeriodDropdown
                        forecastPeriods={selectedForecast?.forecastPeriods || []}
                        selectedForecastPeriodId={selectedForecastPeriodId}
                        onForecastPeriodSelected={handleForecastPeriodSelected}
                    />
                )}
            </Box>

            {selectedForecastId && selectedForecastPeriodId && (
                <>
                    <Card variant="outlined" sx={{ borderRadius: 2, padding: "1rem", mb: 3 }}>
                        <Typography variant="h6" align="left" gutterBottom>
                            Gross Revenue (£)
                        </Typography>
                        {loadingChart ? (
                            <Skeleton variant="rectangular" width="100%" height={300} />
                        ) : (
                            <BarChart
                                height={300}
                                dataset={dataset}
                                xAxis={[
                                    {
                                        dataKey: "label",
                                        label: "Months",
                                        scaleType: "band",
                                    },
                                ]}
                                series={mainChartData?.map((s) => ({
                                    dataKey: s.label,
                                    label: s.label,
                                })) || []}
                                margin={{ left: 80, right: 20, top: 20, bottom: 20 }}
                            />
                        )}
                    </Card>

                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} xl={6}>
                            <Card variant="outlined" sx={{ borderRadius: 2, padding: "1rem", height: "100%" }}>
                                <Typography variant="h6" align="left" gutterBottom>
                                    YTD Top Customers
                                </Typography>
                                {loadingChart ? (
                                    <Skeleton variant="rectangular" width="100%" height={300} />
                                ) : topCustomersData.length > 0 ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                                        <Box sx={{ flex: '0 0 200px', display: 'flex', justifyContent: 'center' }}>
                                            <PieChart
                                                width={200}
                                                height={200}
                                                series={[
                                                    {
                                                        data: topCustomersData.map((customer, index) => ({
                                                            id: customer.customerId,
                                                            value: customer.value,
                                                            label: customer.customerName,
                                                            color: pieChartColors[index % pieChartColors.length],
                                                        })),
                                                        highlightScope: { faded: "global", highlighted: "item" },
                                                        faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                                                        cx: 100,
                                                        cy: 100,
                                                        innerRadius: 0,
                                                        outerRadius: 80,
                                                    },
                                                ]}
                                                margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                                                slotProps={{
                                                    legend: { hidden: true },
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ pl: 2 }}>
                                            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                                                {topCustomersData.map((customer, index) => (
                                                    <Box
                                                        component="li"
                                                        key={customer.customerId}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            mb: 1,
                                                            fontSize: '0.875rem',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: '50%',
                                                                backgroundColor: pieChartColors[index % pieChartColors.length],
                                                                mr: 1,
                                                                flexShrink: 0,
                                                                mt: '4px',
                                                            }}
                                                        />
                                                        <Typography variant="body2" sx={{ flexGrow: 1, mr: 3, width: 280 }}>
                                                            {customer.customerName}
                                                        </Typography>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                {formatCurrency(customer.value)}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {formatUnits(customer.units)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="textSecondary">
                                        No customer data available
                                    </Typography>
                                )}
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} xl={6}>
                            <Card variant="outlined" sx={{ borderRadius: 2, padding: "1rem", height: "100%" }}>
                                <Typography variant="h6" align="left" gutterBottom>
                                    YTD Top Products
                                </Typography>
                                {loadingChart ? (
                                    <Skeleton variant="rectangular" width="100%" height={300} />
                                ) : topSkusData.length > 0 ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                                        <Box sx={{ flex: '0 0 200px', display: 'flex', justifyContent: 'center' }}>
                                            <PieChart
                                                width={200}
                                                height={200}
                                                series={[
                                                    {
                                                        data: topSkusData.map((sku, index) => ({
                                                            id: sku.productId,
                                                            value: sku.value,
                                                            label: sku.skuDescription,
                                                            color: pieChartColors[index % pieChartColors.length],
                                                        })),
                                                        highlightScope: { faded: "global", highlighted: "item" },
                                                        faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                                                        cx: 100,
                                                        cy: 100,
                                                        innerRadius: 0,
                                                        outerRadius: 80,
                                                    },
                                                ]}
                                                margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                                                slotProps={{
                                                    legend: { hidden: true },
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ pl: 2 }}>
                                            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                                                {topSkusData.map((sku, index) => (
                                                    <Box
                                                        component="li"
                                                        key={sku.productId}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            mb: 1,
                                                            fontSize: '0.875rem',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: '50%',
                                                                backgroundColor: pieChartColors[index % pieChartColors.length],
                                                                mr: 1,
                                                                flexShrink: 0,
                                                                mt: '4px',
                                                            }}
                                                        />
                                                        <Typography variant="body2" sx={{ flexGrow: 1, mr: 3, width: 280 }}>
                                                            {sku.skuDescription}
                                                        </Typography>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                {formatCurrency(sku.value)}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {formatUnits(sku.units)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="textSecondary">
                                        No SKU data available
                                    </Typography>
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default Home; 